import { useNotification } from "app/NotificationProvider";
import { AnalyticsEvents, analytics } from "common/analytics";
import EventFormDialog from "common/components/EventFormDialog";
import { useTranslation } from "common/locales";
import {
  GENERAL_FEED_TYPE,
  GENERAL_FEED_UNITS,
  GeneralFeedPurchaseEvent,
  PURCHASE_CURRENCY,
  useUpdateFeedPurchaseEvent,
} from "model";
import { Dispatch, SetStateAction, useState } from "react";
import { useAppStore } from "stores";
import { FeedPurchaseSingleStep } from "./EnterFeedPurchaseEvent";
import { checkConsistency } from "./feedPurchaseValidation";

export default function UpdateFeedPurchaseEvent({
  feedPurchaseEvent,
  dialogOpen,
  setDialogOpen,
}: {
  feedPurchaseEvent: GeneralFeedPurchaseEvent;
  dialogOpen: boolean;
  setDialogOpen: Dispatch<SetStateAction<boolean>>;
}) {
  const { notify } = useNotification();
  const { t } = useTranslation();
  const updateFeedPurchaseEventMutation = useUpdateFeedPurchaseEvent();

  const currentFarmId = useAppStore((state) => state.currentFarmId);

  const handleClose = () => {
    setDialogOpen(false);
  };

  const [feedType, setFeedType] = useState<GENERAL_FEED_TYPE | undefined>(
    feedPurchaseEvent.feedPurchases[0].feedType
  );

  const [feedTypeError, setFeedTypeError] = useState<boolean>(false);

  const [feedUnit, setFeedUnit] = useState<GENERAL_FEED_UNITS | undefined>(
    feedPurchaseEvent.feedPurchases[0].unitType
  );

  const [feedUnitError, setFeedUnitError] = useState<boolean>(false);

  const [notes, setNotes] = useState<string | undefined>(
    feedPurchaseEvent.note
  );

  const [cost, setCost] = useState<string | undefined>(
    feedPurchaseEvent.feedPurchases[0].cost?.toString()
  );
  const [costError, setCostError] = useState<boolean>(false);
  const [feedAmount, setFeedAmount] = useState<string | undefined>(
    feedPurchaseEvent.feedPurchases[0].amount?.toString()
  );
  const [feedAmountError, setFeedAmountError] = useState<boolean>(false);

  const [reseedDate, setReseedDate] = useState<Date | null>(
    new Date(feedPurchaseEvent.feedPurchaseEventDate)
  );

  const [reseedDateError, setReseedDateError] = useState<boolean>(false);

  const hasChanges = () => {
    return (
      feedType !== feedPurchaseEvent.feedPurchases[0].feedType ||
      feedUnit !== feedPurchaseEvent.feedPurchases[0].unitType ||
      cost !== feedPurchaseEvent.feedPurchases[0].cost?.toString() ||
      feedAmount !== feedPurchaseEvent.feedPurchases[0].amount?.toString() ||
      notes !== feedPurchaseEvent.note
    );
  };

  async function callUpdate() {
    return await updateFeedPurchaseEventMutation.mutateAsync({
      farmGuid: currentFarmId ?? "",
      guid: feedPurchaseEvent.guid ?? "",
      note: notes ?? "",
      feedPurchaseEventDate: reseedDate?.toISOString() ?? "",
      feedPurchases: [
        {
          costCurrency: cost === undefined ? undefined : PURCHASE_CURRENCY.GBP,
          cost: Number(cost),
          unitType: feedUnit ?? GENERAL_FEED_UNITS.TONNE,
          feedType: feedType ?? GENERAL_FEED_TYPE.CONCENTRATED_FEED,
          amount: Number(feedAmount),
        },
      ],
    });
  }

  async function handleSave() {
    const consistent = checkConsistency(
      feedType,
      setFeedTypeError,
      feedUnit,
      setFeedUnitError,
      reseedDate,
      setReseedDateError,
      reseedDateError,
      feedAmount,
      setFeedAmountError,
      cost,
      setCostError
    );
    // check for wrong input
    if (!consistent) {
      return;
    }

    await callUpdate();
    notify(t("updateFeedPurchase.success"));
    analytics.track(AnalyticsEvents.FEED_PURCHASE_UPDATED);
    handleClose();
  }

  return (
    <EventFormDialog
      open={dialogOpen}
      onClose={handleClose}
      title={t("enterFeedPurchase.heading")}
      onSave={handleSave}
      isDirty={hasChanges}
      cancelAnalyticsEvent={AnalyticsEvents.FEED_PURCHASE_UPDATE_CANCELLED}
      steps={[
        {
          content: (
            <FeedPurchaseSingleStep
              date={reseedDate}
              setDate={setReseedDate}
              dateError={reseedDateError}
              feedType={feedType}
              setFeedType={setFeedType}
              feedTypeError={feedTypeError}
              setFeedTypeError={setFeedTypeError}
              feedUnit={feedUnit}
              setFeedUnit={setFeedUnit}
              feedUnitError={feedUnitError}
              setFeedUnitError={setFeedUnitError}
              feedAmount={feedAmount}
              setFeedAmount={setFeedAmount}
              feedAmountError={feedAmountError}
              setFeedAmountError={setFeedAmountError}
              cost={cost}
              setCost={setCost}
              costError={costError}
              setCostError={setCostError}
              note={notes}
              setNotes={setNotes}
            />
          ),
        },
      ]}
    />
  );
}
